
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { AlertCircle, CheckCircle, RefreshCw, FolderArchive, HardDrive, Download } from "lucide-react";
import { testWordPressPluginConnection } from "@/utils/wordpressApiExtended";
import { BackupResult } from "@/utils/wordpressApi";
import { toast } from "@/hooks/use-toast";
import { getBackupDirectoryPath, downloadBackupFile, checkBackupExists } from "@/utils/downloadUtils";

interface PluginBackupMonitorProps {
  websiteId?: string;
  backupId?: string;
  siteUrl?: string;
}

export const PluginBackupMonitor = ({ 
  websiteId = "4", 
  backupId,
  siteUrl = "https://www.drive-expert.com" 
}: PluginBackupMonitorProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState<string | null>(null);
  const [backupDetails, setBackupDetails] = useState<BackupResult | null>(null);
  const [logs, setLogs] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isBackupExists, setIsBackupExists] = useState(false);
  
  // Function to add logs with timestamps
  const addLog = (message: string) => {
    const timestamp = new Date().toISOString().substring(11, 19);
    setLogs(prev => [...prev, `${timestamp} - ${message}`]);
  };

  // Check if backup exists
  useEffect(() => {
    if (backupId) {
      checkBackupExists(backupId, siteUrl)
        .then(exists => setIsBackupExists(exists))
        .catch(() => setIsBackupExists(false));
    }
  }, [backupId, siteUrl]);

  // Simulate checking the backup status
  const checkBackupStatus = async () => {
    setIsLoading(true);
    addLog("Checking backup status...");
    
    try {
      // Simulate connection test
      const connected = await testWordPressPluginConnection({
        siteId: websiteId,
        apiKey: "demo_api_key_for_testing",
        siteUrl: siteUrl
      });
      
      if (!connected) {
        setError("Failed to connect to WordPress site");
        addLog("ERROR: Connection failed");
        setStatus("error");
        setIsLoading(false);
        return;
      }
      
      addLog("Connection successful");
      
      // Log backup directory information
      addLog(`Backup directory: ${getBackupDirectoryPath()}`);
      
      // Check if backup exists
      if (backupId) {
        const exists = await checkBackupExists(backupId, siteUrl);
        if (exists) {
          addLog(`Found existing backup ${backupId}.zip`);
          setIsBackupExists(true);
          setStatus("completed");
          setProgress(100);
          setBackupDetails({
            id: backupId,
            createdAt: new Date().toISOString(),
            status: "completed",
            size: "Calculating...",
            progress: 100,
            downloadUrl: `${siteUrl}/wp-json/websitebackup/v1/backup/${backupId}/download`
          });
          setIsLoading(false);
          return;
        }
      }
      
      // Simulate getting backup info - in real implementation this would call the WordPress REST API
      if (!backupId) {
        addLog("No backup in progress");
        setStatus("idle");
        setIsLoading(false);
        return;
      }
      
      // Simulate progress updates
      let currentProgress = progress;
      const interval = setInterval(() => {
        if (currentProgress < 100) {
          currentProgress += 10;
          setProgress(currentProgress);
          addLog(`Backup progress: ${currentProgress}%`);
          
          if (currentProgress === 30) {
            addLog(`Creating backup directory at ${getBackupDirectoryPath()}`);
          }
          
          if (currentProgress === 60) {
            addLog(`Writing backup file to ${getBackupDirectoryPath()}${backupId}.zip`);
          }
          
          if (currentProgress === 100) {
            setStatus("completed");
            setIsBackupExists(true);
            addLog(`Backup completed successfully and stored at ${getBackupDirectoryPath()}${backupId}.zip`);
            
            // Update backup details with download URL
            setBackupDetails(prev => prev ? {
              ...prev,
              status: "completed",
              downloadUrl: `${siteUrl}/wp-json/websitebackup/v1/backup/${backupId}/download`
            } : null);
            
            clearInterval(interval);
          } else {
            setStatus("in-progress");
          }
        } else {
          clearInterval(interval);
        }
      }, 1500);
      
      // Simulate backup details
      setBackupDetails({
        id: backupId,
        createdAt: new Date().toISOString(),
        status: "in-progress",
        size: "Calculating...",
        progress: currentProgress,
        downloadUrl: currentProgress === 100 ? `${siteUrl}/wp-json/websitebackup/v1/backup/${backupId}/download` : undefined
      });
      
    } catch (err) {
      console.error("Error checking backup status:", err);
      setError("An error occurred while checking backup status");
      addLog(`ERROR: ${err instanceof Error ? err.message : 'Unknown error'}`);
      setStatus("error");
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle backup download
  const handleDownloadBackup = async () => {
    if (!backupId) return;
    
    addLog(`Downloading backup ${backupId}.zip...`);
    await downloadBackupFile(backupId, siteUrl);
    addLog(`Download initiated for backup ${backupId}.zip`);
  };
  
  // Check status initially if a backupId is provided
  useEffect(() => {
    if (backupId) {
      checkBackupStatus();
    } else {
      setStatus("idle");
      addLog("Waiting for backup to start");
    }
  }, [backupId]);
  
  // Restart monitoring
  const handleRefresh = () => {
    setProgress(0);
    setStatus(null);
    setBackupDetails(null);
    setLogs([]);
    setError(null);
    setIsBackupExists(false);
    checkBackupStatus();
  };
  
  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="text-lg font-semibold flex items-center">
          <FolderArchive className="mr-2 h-5 w-5" />
          Backup Progress Monitor
        </CardTitle>
      </CardHeader>
      <CardContent>
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 flex items-center">
            <AlertCircle className="h-5 w-5 mr-2" />
            {error}
          </div>
        )}
        
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium">
              {status === "idle" && "Waiting to start backup"}
              {status === "in-progress" && "Backup in progress"}
              {status === "completed" && "Backup completed"}
              {status === "error" && "Backup failed"}
            </div>
            <div className="text-sm font-medium">
              {progress}%
            </div>
          </div>
          
          <Progress
            value={progress}
            className="h-2"
            aria-label="Backup progress"
          />
        </div>
        
        {backupDetails && (
          <div className="mb-6 p-4 bg-gray-50 rounded-md space-y-2">
            <div className="grid grid-cols-2 text-sm">
              <span className="text-gray-500">Backup ID:</span>
              <span className="font-mono">{backupDetails.id}</span>
              
              <span className="text-gray-500">Created:</span>
              <span>{new Date(backupDetails.createdAt).toLocaleString()}</span>
              
              <span className="text-gray-500">Status:</span>
              <span>{backupDetails.status}</span>
              
              <span className="text-gray-500">Size:</span>
              <span>{backupDetails.size}</span>
              
              <span className="text-gray-500">Storage Location:</span>
              <span className="font-mono flex items-center">
                <HardDrive className="h-3 w-3 mr-1" />
                {getBackupDirectoryPath()}
              </span>

              {isBackupExists && backupDetails.downloadUrl && (
                <>
                  <span className="text-gray-500">Download:</span>
                  <span>
                    <Button 
                      variant="link" 
                      className="p-0 h-auto text-blue-600" 
                      onClick={handleDownloadBackup}
                    >
                      <Download className="h-3 w-3 mr-1" />
                      Download Backup
                    </Button>
                  </span>
                </>
              )}
            </div>
          </div>
        )}
        
        <div className="border rounded-md">
          <div className="bg-gray-100 px-4 py-2 border-b">
            <h3 className="text-sm font-medium">Backup Logs</h3>
          </div>
          <div className="p-4 bg-black text-white font-mono text-xs h-40 overflow-y-auto">
            {logs.length > 0 ? (
              logs.map((log, i) => (
                <div key={i} className="py-1">
                  {log}
                </div>
              ))
            ) : (
              <div className="text-gray-400">No logs available</div>
            )}
          </div>
        </div>
        
        <div className="mt-4 space-x-2 flex justify-end">
          <Button 
            variant="outline" 
            size="sm"
            disabled={isLoading}
            onClick={handleRefresh}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
          {status === "completed" && (
            <>
              <Button 
                variant="default" 
                size="sm"
                onClick={() => {
                  toast({
                    title: "Backup verification complete",
                    description: "Your backup completed successfully and was verified"
                  });
                }}
              >
                <CheckCircle className="mr-2 h-4 w-4" />
                Verify Backup
              </Button>
              {isBackupExists && (
                <Button 
                  variant="secondary" 
                  size="sm"
                  onClick={handleDownloadBackup}
                >
                  <Download className="mr-2 h-4 w-4" />
                  Download Backup
                </Button>
              )}
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
